import { CSSProperties, useMemo, useState } from 'react';
import { useAuth } from '@territory/client/AuthContext';
import { API, apiUrl } from '@territory/client/routes';
import { Splash } from './Splash';
import { detectInAppBrowser, tryOpenInSystemBrowser } from './wedgeInAppBrowser';

/**
 * Internal-only grant console — wedge.repvantage.io/grant.
 *
 * A RepVantage team member opens this (typically from the link in a request
 * email), and generates a one-time access code for an approved outside
 * requester. The POST to /api/auth/wedge-access/grant is AuthGuard-protected
 * and the backend re-verifies the caller is on the internal domain, so an
 * external user who reaches this page cannot actually mint a code.
 *
 * This route is mounted OUTSIDE WedgeAuthGate (it must render for a signed-in
 * internal user without dropping them into PREP), so it owns its own auth
 * handling via useAuth().
 */
export function WedgeGrant() {
  const { isLoading, isAuthenticated } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const [email, setEmail] = useState(params.get('email') || '');
  const [name, setName] = useState(params.get('name') || '');
  const [company, setCompany] = useState(params.get('company') || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState<{
    code: string;
    email: string;
    expiresInHours: number;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  /* 2026-07-13 · in-app webview guard · a grant link tapped inside the
     Gmail-app webview would dead-end on Google's disallowed_useragent
     403. Steer to the system browser instead of redirecting. */
  const inAppInfo = useMemo(() => detectInAppBrowser(), []);
  const [escapeTried, setEscapeTried] = useState(false);

  const screen: CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    padding: '32px 20px',
    textAlign: 'center',
    background:
      'radial-gradient(58% 55% at 50% 32%, rgba(52,211,153,0.20) 0%, rgba(52,211,153,0.06) 46%, rgba(13,27,17,0) 72%), radial-gradient(70% 50% at 84% 8%, rgba(245,158,11,0.06) 0%, rgba(13,27,17,0) 60%), linear-gradient(160deg, #0C1E15 0%, #091210 55%, #0D1B11 100%)',
    color: '#E2E8F0',
    fontFamily: "'Jost', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
  };
  const field: CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    marginBottom: 10,
    padding: '13px 15px',
    borderRadius: 12,
    border: '1px solid rgba(52,211,153,0.18)',
    background: 'rgba(52,211,153,0.05)',
    color: '#E2E8F0',
    fontSize: 15,
    fontFamily: 'inherit',
    outline: 'none',
  };

  if (isLoading) return <Splash label="Checking your session" />;

  if (!isAuthenticated) {
    return (
      <div style={screen}>
        <img src="/wedge/repvantage-radar.svg?v=20260712" alt="RepVantage" width={56} height={56} />
        <div style={{ fontSize: 18, fontWeight: 700, color: '#E2E8F0' }}>Grant console</div>
        <div style={{ fontSize: 14, color: '#94A3B8', maxWidth: 320, lineHeight: 1.5 }}>
          Sign in with your @repvantage.io account to issue a PREP access code.
        </div>
        {inAppInfo.inApp && (
          <div
            style={{
              fontSize: 12.5,
              padding: '10px 12px',
              borderRadius: 10,
              background: 'rgba(245,158,11,0.12)',
              border: '1px solid rgba(245,158,11,0.40)',
              color: '#FFD380',
              maxWidth: 320,
              lineHeight: 1.5,
            }}
          >
            You&rsquo;re inside an app&rsquo;s built-in browser · Google blocks sign-in
            here. {escapeTried
              ? 'If nothing opened, use the share menu and choose Open in Browser.'
              : 'The button below will try to open this page in your real browser.'}
          </div>
        )}
        <button
          onClick={() => {
            if (inAppInfo.inApp) {
              tryOpenInSystemBrowser(inAppInfo);
              setEscapeTried(true);
              return;
            }
            window.location.assign(`${apiUrl(API.AUTH.GOOGLE)}?app=wedge`);
          }}
          style={{
            marginTop: 6,
            padding: '12px 22px',
            borderRadius: 14,
            border: 0,
            background: '#34d399',
            color: '#06281c',
            fontSize: 13.5,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            boxShadow: '0 6px 18px rgba(52,211,153,0.28)',
          }}
        >
          Sign in with Google
        </button>
        <div style={{ fontSize: 12, color: '#64748B', maxWidth: 300, lineHeight: 1.5 }}>
          After signing in, reopen the grant link from the request email.
        </div>
      </div>
    );
  }

  const generate = async () => {
    setErr('');
    const e = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      setErr('Enter a valid email address to grant.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(apiUrl('/api/auth/wedge-access/grant'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: e, name: name.trim(), company: company.trim() }),
      });
      const j = (await res.json().catch(() => ({}))) as {
        code?: string;
        email?: string;
        expiresInDays?: number;
        expiresInHours?: number;
        message?: string;
      };
      if (!res.ok || !j.code) {
        throw new Error(j?.message || `Could not generate a code (${res.status}).`);
      }
      setResult({
        code: j.code,
        email: j.email || e,
        expiresInHours: j.expiresInHours ?? (j.expiresInDays ? j.expiresInDays * 24 : 48),
      });
      setCopied(false);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Could not generate a code.');
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div style={screen}>
        <img src="/wedge/repvantage-radar.svg?v=20260712" alt="RepVantage" width={48} height={48} />
        <div style={{ fontSize: 13, color: '#94A3B8' }}>
          Access code for <b style={{ color: '#E2E8F0' }}>{result.email}</b>
        </div>
        <div
          style={{
            fontSize: 30,
            fontWeight: 800,
            letterSpacing: 3,
            fontFamily: "'Share Tech Mono', ui-monospace, Menlo, monospace",
            color: '#F59E0B',
            background: 'rgba(245,158,11,0.10)',
            border: '1px solid rgba(245,158,11,0.45)',
            borderRadius: 14,
            padding: '16px 24px',
          }}
        >
          {result.code}
        </div>
        <button
          onClick={() => {
            navigator.clipboard?.writeText(result.code).then(
              () => setCopied(true),
              () => setCopied(false),
            );
          }}
          style={{
            padding: '10px 20px',
            borderRadius: 14,
            border: '1px solid rgba(52,211,153,0.18)',
            background: 'rgba(52,211,153,0.05)',
            color: '#E2E8F0',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {copied ? 'Copied' : 'Copy code'}
        </button>
        <div style={{ fontSize: 13, color: '#94A3B8', maxWidth: 320, lineHeight: 1.55 }}>
          Send this code to {result.email}. They enter it with their email at
          wedge.repvantage.io. Single-use, expires in {result.expiresInHours} hours,
          and grants <b style={{ color: '#E2E8F0' }}>3 free PREP searches</b> over
          the next {result.expiresInHours}-hour window.
        </div>
        <button
          onClick={() => {
            setResult(null);
            setEmail('');
            setName('');
            setCompany('');
          }}
          style={{
            marginTop: 4,
            background: 'none',
            border: 0,
            color: '#94A3B8',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Grant another
        </button>
      </div>
    );
  }

  return (
    <div style={screen}>
      <img src="/wedge/repvantage-radar.svg?v=20260712" alt="RepVantage" width={52} height={52} />
      <div style={{ fontSize: 18, fontWeight: 700, color: '#E2E8F0' }}>Grant PREP access</div>
      <div style={{ fontSize: 13, color: '#94A3B8', maxWidth: 320, lineHeight: 1.5 }}>
        Generate a one-time access code for an approved requester.
      </div>
      <div style={{ width: '100%', maxWidth: 340, textAlign: 'left', marginTop: 4 }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Requester's email"
          autoCapitalize="off"
          style={field}
        />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name (optional)"
          style={field}
        />
        <input
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Company (optional)"
          style={field}
        />
        {err && (
          <div
            style={{ color: '#FCA5A5', fontSize: 12.5, margin: '2px 0 10px', lineHeight: 1.4 }}
          >
            {err}
          </div>
        )}
        <button
          onClick={generate}
          disabled={busy}
          style={{
            width: '100%',
            padding: 14,
            borderRadius: 14,
            border: 0,
            background: busy ? 'rgba(52,211,153,0.42)' : '#34d399',
            color: '#06281c',
            fontSize: 14.5,
            fontWeight: 700,
            cursor: busy ? 'default' : 'pointer',
            fontFamily: 'inherit',
            boxShadow: busy ? 'none' : '0 6px 18px rgba(52,211,153,0.28)',
          }}
        >
          {busy ? 'Generating…' : 'Generate access code'}
        </button>
      </div>
    </div>
  );
}
